import { accountKeys, entryKeys, type AccountNode, type EntryListQuery } from '@taler/api-client'
import { useInfiniteQuery, useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useAccountingView } from '@/app/accounting-view'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { keepPreviousListResult, sameLedger } from '@/app/keep-previous-list'
import { useDisplayCurrency } from '@/app/display-currency'
import { indexAccounts, toTransactionRow, type TransactionRow } from '../internal/transaction-row.ts'
import { type CrossLedgerLinksByEntryId, useCrossLedgerLinks } from './use-cross-ledger-links.ts'

export const TRANSACTIONS_PAGE_SIZE = 25

export interface UseTransactionsOptions {
  readonly ledgerId: () => string | undefined
  readonly locale: () => 'en' | 'hu'
  /** The screen never issues a request for a capability the ledger did not grant. */
  readonly enabled: () => boolean
  readonly filters: () => EntryListQuery
}

export interface UseTransactions {
  readonly rows: ComputedRef<readonly TransactionRow[]>
  readonly crossLedgerLinks: ComputedRef<CrossLedgerLinksByEntryId>
  readonly accountTree: ComputedRef<readonly AccountNode[]>
  readonly isLoading: ComputedRef<boolean>
  readonly isFetchingNextPage: ComputedRef<boolean>
  readonly hasNextPage: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  fetchNextPage(): void
  retry(): void
}

export function useTransactions(options: UseTransactionsOptions): UseTransactions {
  const api = useApi()
  const { accountingView } = useAccountingView()
  const { displayCurrency } = useDisplayCurrency()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)
  const listQuery = computed<EntryListQuery>(() => ({
    limit: TRANSACTIONS_PAGE_SIZE,
    ...options.filters(),
    displayCurrency: displayCurrency.value as NonNullable<EntryListQuery['displayCurrency']>,
  }))

  const accountsQuery = useQuery({
    queryKey: computed(() => accountKeys.tree(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.accounts.tree(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  const entriesQuery = useInfiniteQuery({
    queryKey: computed(() => entryKeys.list(options.ledgerId() ?? '', listQuery.value)),
    queryFn: ({ pageParam, signal }) =>
      api.entries.list(
        options.ledgerId()!,
        {
          ...listQuery.value,
          ...(pageParam === undefined ? {} : { cursor: pageParam as string }),
        },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: active,
    retry: false,
    placeholderData: computed(() => keepPreviousListResult(sameLedger(options.ledgerId()))),
  })

  const accountIndex = computed(() => indexAccounts(accountsQuery.data.value ?? []))
  const locale = computed(() => options.locale())

  const rows = computed(() =>
    (entriesQuery.data.value?.pages ?? []).flatMap((page) =>
      page.items.map((entry) =>
        toTransactionRow(
          entry,
          accountIndex.value,
          locale.value,
          accountingView.value,
          displayCurrency.value,
        ),
      ),
    ),
  )

  const crossLedgerLinks = useCrossLedgerLinks({
    ledgerId: options.ledgerId,
    entryIds: () => rows.value.map((row) => row.id),
    enabled: () => active.value,
  })

  return {
    rows,
    crossLedgerLinks,
    accountTree: computed(() => accountsQuery.data.value ?? []),
    isLoading: computed(
      () => active.value && (entriesQuery.isPending.value || accountsQuery.isPending.value),
    ),
    isFetchingNextPage: computed(() => entriesQuery.isFetchingNextPage.value),
    hasNextPage: computed(() => entriesQuery.hasNextPage.value),
    error: computed(() => entriesQuery.error.value ?? accountsQuery.error.value),
    retrying: retrying(entriesQuery, accountsQuery),
    fetchNextPage: () => {
      void entriesQuery.fetchNextPage()
    },
    retry: () => {
      void entriesQuery.refetch()
      void accountsQuery.refetch()
    },
  }
}
